import { ExportFatalError } from './types';
import type { ExportItem, ExportOutcome, ExportProgress, ExportResult, ExportSink } from './types';

export interface RunOptions {
  signal: AbortSignal;
  concurrency?: number;
  onProgress?: (progress: ExportProgress) => void;
}

/**
 * Drives items through a sink a few at a time. One item failing does not stop the run;
 * a cancel or an ExportFatalError does. Always resolves, and always calls sink.finish once.
 */
export async function runExport(items: ExportItem[], sink: ExportSink, options: RunOptions): Promise<ExportResult> {
  const { signal, concurrency = 4, onProgress } = options;
  const saved = new Set<ExportItem>();
  const skipped = new Set<ExportItem>();
  const failed = new Set<ExportItem>();
  const run = { next: 0, fatal: null as string | null };

  const report = () => onProgress?.({
    total: items.length,
    done: saved.size + skipped.size + failed.size,
    saved: saved.size,
    skipped: skipped.size,
    failed: failed.size,
  });

  const worker = async () => {
    while (!signal.aborted && run.fatal === null && run.next < items.length) {
      const item = items[run.next];
      run.next += 1;
      try {
        if (await sink.has(item)) {
          skipped.add(item);
        } else {
          await sink.write(item, signal);
          saved.add(item);
        }
      } catch (err) {
        if (err instanceof ExportFatalError) {
          run.fatal = err.message;
          return;
        }
        if (signal.aborted) return;
        failed.add(item);
      }
      report();
    }
  };

  report();
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, worker));

  let outcome: ExportOutcome = run.fatal !== null ? 'fatal' : signal.aborted ? 'cancelled' : 'complete';
  try {
    await sink.finish(outcome);
  } catch (err) {
    if (outcome === 'complete') {
      outcome = 'fatal';
      run.fatal = err instanceof Error ? err.message : 'The export could not be finished.';
    }
  }

  const inOrder = (set: Set<ExportItem>) => items.filter(i => set.has(i));
  return { saved: inOrder(saved), skipped: inOrder(skipped), failed: inOrder(failed), outcome, fatalMessage: run.fatal };
}
